import { z } from 'zod'

/**
 * システム管理者ダッシュボード用 Zod スキーマ。
 *
 * 関連: docs/system-requirements.md §4.10, packages/db/prisma/schema.prisma の Tenant / AdminAuditLog
 */

// ----- Tenant -----

export const TenantKindSchema = z.enum(['REGULAR', 'EPHEMERAL'])
export const PlanSchema = z.enum(['FREE', 'PRO', 'EPHEMERAL'])
export const TenantStatusSchema = z.enum(['ACTIVE', 'SUSPENDED', 'FROZEN'])

/** テナントを識別するための代表ユーザー情報（管理者画面の一覧で「誰のテナントか」を見るため）。 */
const AdminTenantOwnerSchema = z.object({
  email: z.string(),
  isAdmin: z.boolean(),
  lastLoginAt: z.string().datetime().nullable(),
})
export type AdminTenantOwner = z.infer<typeof AdminTenantOwnerSchema>

export const AdminTenantSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  kind: TenantKindSchema,
  plan: PlanSchema,
  status: TenantStatusSchema,
  storageUsedBytes: z.string(), // BigInt → string
  addonStorageTb: z.number().int(),
  expiresAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  /**
   * テナント所属ユーザー（管理者一覧で代表メアドを表示するため）。
   * - REGULAR: lastLoginAt 降順で最大 3 人。代表メアドは items[0].email
   * - EPHEMERAL: 空配列（public.users を持たない）
   */
  members: z.array(AdminTenantOwnerSchema),
  /** 所属ユーザー総数（members は最大 3 人なので「+N 人」表示用） */
  memberCount: z.number().int().nonnegative(),
})
export type AdminTenant = z.infer<typeof AdminTenantSchema>

export const ListAdminTenantsQuerySchema = z.object({
  plan: PlanSchema.optional(),
  status: TenantStatusSchema.optional(),
  kind: TenantKindSchema.optional(),
  /** テナント名・所属ユーザーメアドの部分一致検索（大小無視） */
  q: z.string().trim().min(1).max(100).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(200).default(50),
})
export type ListAdminTenantsQuery = z.infer<typeof ListAdminTenantsQuerySchema>

export const ListAdminTenantsResultSchema = z.object({
  items: z.array(AdminTenantSchema),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
})
export type ListAdminTenantsResult = z.infer<typeof ListAdminTenantsResultSchema>

export const AdminTenantIdParamsSchema = z.object({
  id: z.string().uuid(),
})

// ----- プラン手動切替 -----

/**
 * プラン変更入力。
 *
 * NOTE: `addonStorageTb` は将来の拡張用に optional として残しているが、
 * 現行の UI からは入力しない（追加ストレージ機能はオフ）。
 * DB カラム `tenants.addon_storage_tb` も既存値を保つために維持している。
 */
export const UpdateTenantPlanInputSchema = z.object({
  plan: z.enum(['FREE', 'PRO']),
  addonStorageTb: z.number().int().min(0).max(1000).optional(),
})
export type UpdateTenantPlanInput = z.infer<typeof UpdateTenantPlanInputSchema>

// ----- 監査ログ（テナント単位閲覧のみ） -----
//
// 全テナント横断の監査ログ閲覧は提供しない。テナント詳細画面から
// `GET /admin/tenants/:id/audit-logs` で当該テナントに関連するログのみを取得する。
// 記録自体は asAdmin ヘルパーが admin_audit_logs に書き込むので変更なし。

export const AdminAuditLogSchema = z.object({
  id: z.string(),
  adminUserId: z.string().uuid(),
  /** 管理者のメールアドレス（DB から解決済み）。削除済みなら null */
  adminUserEmail: z.string().nullable(),
  action: z.string(),
  targetTenantId: z.string().uuid().nullable(),
  /** 生の targetResource (`"file:<uuid>"` / `"project:<uuid>"` 等)。互換用に残す */
  targetResource: z.string().nullable(),
  /** targetResource を解析した種別。表示用に推奨。 */
  targetKind: z.enum(['file', 'project']).nullable(),
  /** 解析した ID 部分（UUID）。 */
  targetId: z.string().nullable(),
  /** プロジェクト名 / ファイル名（DB から解決済み）。削除済みなら null */
  targetName: z.string().nullable(),
  /** ファイル種別アイコン用 MIME。プロジェクトの場合は null */
  targetMimeType: z.string().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.string().datetime(),
})
export type AdminAuditLog = z.infer<typeof AdminAuditLogSchema>

export const ListTenantAuditLogsQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(200).default(50),
})

export const ListTenantAuditLogsResultSchema = z.object({
  items: z.array(AdminAuditLogSchema),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
})
export type ListTenantAuditLogsResult = z.infer<typeof ListTenantAuditLogsResultSchema>

// ----- ステルス: テナント配下プロジェクト一覧 -----

export const AdminTenantProjectSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  fileCount: z.number().int().nonnegative(),
})
export const AdminTenantProjectsResultSchema = z.object({
  items: z.array(AdminTenantProjectSchema),
})
export type AdminTenantProjectsResult = z.infer<typeof AdminTenantProjectsResultSchema>

// ----- ステルス: プロジェクト配下ファイル一覧 -----

export const AdminProjectFileSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  originalName: z.string(),
  sizeBytes: z.string(), // BigInt → string
  durationSec: z.number().nullable(),
  mimeType: z.string(),
  thumbnailKey: z.string().nullable(),
  streamUid: z.string().nullable(),
  streamStatus: z.enum(['PENDING', 'QUEUED', 'READY', 'ERROR']).nullable(),
  archivedAt: z.string().datetime().nullable(),
  uploadedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type AdminProjectFile = z.infer<typeof AdminProjectFileSchema>

export const AdminProjectFilesResultSchema = z.object({
  items: z.array(AdminProjectFileSchema),
})
export type AdminProjectFilesResult = z.infer<typeof AdminProjectFilesResultSchema>

/** ステルス DL URL レスポンス */
export const AdminFileDownloadUrlSchema = z.object({
  url: z.string().url(),
  expiresAt: z.string().datetime(),
})
export type AdminFileDownloadUrl = z.infer<typeof AdminFileDownloadUrlSchema>

/** ステルス Stream 再生トークン レスポンス */
export const AdminFileStreamTokenSchema = z.object({
  token: z.string(),
  expiresAt: z.string().datetime(),
  hlsManifestUrl: z.string().url(),
  iframeUrl: z.string().url(),
})
export type AdminFileStreamToken = z.infer<typeof AdminFileStreamTokenSchema>

// ----- 容量概況ダッシュボード -----

/**
 * 全社サマリ。
 * BRD §6.E「容量概況」/ SRD §10 観測対象に対応。
 */
export const AdminDashboardSummarySchema = z.object({
  /** 集計時刻（UTC ISO8601） */
  generatedAt: z.string().datetime(),
  /** 全テナント合計のストレージ使用量（バイト） */
  totalStorageBytes: z.string(), // BigInt は文字列でやり取りする
  /** プラン別件数 */
  tenantsByPlan: z.object({
    FREE: z.number().int().nonnegative(),
    PRO: z.number().int().nonnegative(),
    EPHEMERAL: z.number().int().nonnegative(),
  }),
  /** ステータス別件数 */
  tenantsByStatus: z.object({
    ACTIVE: z.number().int().nonnegative(),
    FROZEN: z.number().int().nonnegative(),
    SUSPENDED: z.number().int().nonnegative(),
    DELETED: z.number().int().nonnegative(),
  }),
  /** 全テナント合計件数（種別 EPHEMERAL を含む） */
  totalTenants: z.number().int().nonnegative(),
  /** 全プロジェクト件数（archived 含む） */
  totalProjects: z.number().int().nonnegative(),
  /** 全ファイル件数 */
  totalFiles: z.number().int().nonnegative(),
  /** 過去7日の新規テナント数（kind='REGULAR' のみ） */
  newTenantsLast7Days: z.number().int().nonnegative(),
  /** 過去30日の新規テナント数（kind='REGULAR' のみ） */
  newTenantsLast30Days: z.number().int().nonnegative(),
  /** 未対応の有料プラン申請数（管理者ダッシュボードのバッジ用） */
  pendingPlanUpgradeRequests: z.number().int().nonnegative(),
})
export type AdminDashboardSummary = z.infer<typeof AdminDashboardSummarySchema>
